import fs from "node:fs/promises";
import path from "node:path";
import Ajv from "ajv";
import { stripMarkdownFence } from "./workflow-artifacts.js";
import { getPackageRoot } from "../package-assets.js";

export interface TechnicalPlanFile {
  path: string;
  reason: string;
}

export interface TechnicalPlan {
  title: string;
  architectureSummary: string;
  filesInScope: TechnicalPlanFile[];
  filesOutOfScope: TechnicalPlanFile[];
  implementationSteps: string[];
  dependencies: string[];
  risks: string[];
  tradeoffs: string[];
  validationPlan: string[];
  rollbackPlan: string[];
}

export interface TechnicalPlanValidationResult {
  valid: boolean;
  reason?: string;
  plan?: TechnicalPlan;
  content?: string;
}

function isContained(root: string, candidate: string): boolean {
  return candidate === root || candidate.startsWith(`${root}${path.sep}`);
}

export class TechnicalPlanValidator {
  cwd: string;

  constructor({ cwd = process.cwd() }: { cwd?: string } = {}) {
    this.cwd = path.resolve(cwd);
  }

  private async validateScopedPath(candidate: string, forbiddenPaths: Set<string>): Promise<string | null> {
    if (path.isAbsolute(candidate)) return `Absolute path is not allowed: '${candidate}'.`;
    const normalized = candidate.replaceAll("\\", "/");
    if (!normalized || normalized.split("/").some((part) => part === "" || part === "." || part === "..")) {
      return `Path must be normalized without empty, '.' or '..' segments: '${candidate}'.`;
    }
    if (normalized === ".git" || normalized.startsWith(".git/") || normalized === ".ai-workflow" || normalized.startsWith(".ai-workflow/")) {
      return `Workflow control path is out of implementation scope: '${candidate}'.`;
    }
    const conflictsWithProtectedPath = [...forbiddenPaths].some((protectedPath) =>
      normalized === protectedPath ||
      normalized.startsWith(`${protectedPath}/`) ||
      protectedPath.startsWith(`${normalized}/`)
    );
    if (normalized === "EVIDENCE.json" || conflictsWithProtectedPath) {
      return `Protected workflow artifact is out of implementation scope: '${candidate}'.`;
    }

    const absolute = path.resolve(this.cwd, normalized);
    if (!isContained(this.cwd, absolute)) return `Path escapes repository: '${candidate}'.`;

    let cursor = this.cwd;
    for (const part of normalized.split("/")) {
      cursor = path.join(cursor, part);
      try {
        const stat = await fs.lstat(cursor);
        if (cursor === absolute && stat.isDirectory()) {
          return `filesInScope must name explicit files, not a directory: '${candidate}'.`;
        }
        if (stat.isSymbolicLink()) {
          const resolved = await fs.realpath(cursor);
          if (!isContained(this.cwd, resolved)) return `Symlink escapes repository: '${candidate}'.`;
          cursor = resolved;
        }
      } catch (error: any) {
        if (error.code !== "ENOENT") return `Cannot inspect scoped path '${candidate}': ${error.message}`;
        break;
      }
    }
    return null;
  }

  async validateOutput(output: string, protectedPaths: string[] = []): Promise<TechnicalPlanValidationResult> {
    let plan: TechnicalPlan;
    try {
      plan = JSON.parse(stripMarkdownFence(output));
    } catch (error: any) {
      return { valid: false, reason: `Technical plan is not valid JSON: ${error.message}` };
    }

    try {
      const schema = JSON.parse(await fs.readFile(path.join(getPackageRoot(), "dist-assets/schemas/technical-plan.schema.json"), "utf8"));
      const validateSchema = new Ajv({ allErrors: true }).compile(schema);
      if (!validateSchema(plan)) {
        const errors = (validateSchema.errors || []).map((error) => `${error.instancePath} ${error.message}`).join(", ");
        return { valid: false, reason: `Technical plan schema violation: ${errors}` };
      }
    } catch (error: any) {
      return { valid: false, reason: `Technical plan schema validation failed: ${error.message}` };
    }

    if (plan.filesInScope.length === 0) {
      return { valid: false, reason: "Technical plan filesInScope must not be empty." };
    }

    const forbidden = new Set(protectedPaths.map((entry) => path.relative(this.cwd, path.resolve(this.cwd, entry)).replaceAll("\\", "/")));
    for (const entry of plan.filesInScope) {
      const reason = await this.validateScopedPath(entry.path, forbidden);
      if (reason) return { valid: false, reason };
    }

    const content = `${JSON.stringify(plan, null, 2)}\n`;
    return { valid: true, plan, content };
  }
}
